/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  makeStyles,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import { KnativeService, KnativeRevision, TrafficTarget } from '../types';

// 自定义注解 key
const BACKSTAGE_MODIFIED_BY = 'backstage.io/last-modified-by';
const BACKSTAGE_MODIFIED_AT = 'backstage.io/last-modified-at';
const BACKSTAGE_MODIFIED_ACTION = 'backstage.io/last-action';

const useStyles = makeStyles(() => ({
  dialogContent: {
    minWidth: 400,
  },
}));

export interface TrafficSplitDialogProps {
  open: boolean;
  onClose: () => void;
  /** 保存成功后回调（用于刷新数据） */
  onSaved: () => void;
  service: KnativeService | undefined;
  serviceName: string | undefined;
  serviceNamespace: string;
  /** 已解析的目标集群名 */
  clusterName: string;
  revisions: KnativeRevision[];
}

/**
 * 切流对话框（运维应急直写）。
 *
 * 仅修改 spec.traffic 的 scoped merge-PATCH，逻辑对齐 kn service apply：
 * 不碰 spec.template，metadata.annotations 增量合并审计字段。
 *
 * @public
 */
export const TrafficSplitDialog = ({
  open,
  onClose,
  onSaved,
  service,
  serviceName,
  serviceNamespace,
  clusterName,
  revisions,
}: TrafficSplitDialogProps): JSX.Element => {
  const classes = useStyles();
  const kubernetesApi = useApi(kubernetesApiRef);
  const identityApi = useApi(identityApiRef);

  const [editingTraffic, setEditingTraffic] = useState<TrafficTarget[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const trafficTargets = service?.status?.traffic || [];

  // revisions 按创建时间降序（最新在最前）
  const sortedRevisions = [...revisions].sort(
    (a, b) =>
      new Date(b.metadata.creationTimestamp).getTime() -
      new Date(a.metadata.creationTimestamp).getTime(),
  );

  // 打开时初始化编辑状态：若 latestRevision 为 true，转成具体 revision 名称
  useEffect(() => {
    if (!open) return;
    const initialTraffic = trafficTargets.map(t => {
      if (t.latestRevision) {
        const latestRevisionName = service?.status?.latestReadyRevisionName;
        return {
          ...t,
          latestRevision: undefined,
          revisionName: latestRevisionName || t.revisionName,
        };
      }
      return { ...t };
    });
    setEditingTraffic(initialTraffic);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 根据 revision 名称获取创建时间
  const getRevisionCreationTime = (revisionName: string): string => {
    const revision = revisions.find(r => r.metadata.name === revisionName);
    if (!revision) return '';
    return new Date(revision.metadata.creationTimestamp).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // 获取所有可用的 revisions（排除已经在列表中的）
  const getAvailableRevisions = () => {
    const usedRevisionNames = new Set(
      editingTraffic
        .map(t => t.revisionName)
        .filter(Boolean)
    );
    return sortedRevisions.filter(r => !usedRevisionNames.has(r.metadata.name));
  };

  // 添加新的 revision 行
  const handleAddRevision = () => {
    const availableRevisions = getAvailableRevisions();
    if (availableRevisions.length === 0) return;

    const newTraffic: TrafficTarget = {
      revisionName: availableRevisions[0].metadata.name,
      percent: 0,
    };
    setEditingTraffic([...editingTraffic, newTraffic]);
  };

  // 修改 revision 选择
  const handleRevisionChange = (index: number, revisionName: string) => {
    const updated = [...editingTraffic];
    updated[index] = {
      ...updated[index],
      revisionName,
      latestRevision: undefined,
      tag: undefined,
    };
    setEditingTraffic(updated);
  };

  const handleTrafficChange = (index: number, newPercent: number) => {
    const updated = [...editingTraffic];

    // 如果只有一个 revision，直接设置为 100%
    if (updated.length === 1) {
      updated[index] = { ...updated[index], percent: 100 };
      setEditingTraffic(updated);
      return;
    }

    // 限制输入范围
    const clampedPercent = Math.max(0, Math.min(100, newPercent));

    // 更新当前项
    updated[index] = { ...updated[index], percent: clampedPercent };

    // 计算其他项的总和（不包括当前项）
    const otherTotal = updated.reduce(
      (sum, t, i) => (i === index ? sum : sum + (t.percent || 0)),
      0,
    );

    // 计算需要分配给其他项的总量
    const remainingPercent = 100 - clampedPercent;

    // 如果其他项的总和大于剩余百分比，按比例缩减
    // 如果其他项的总和小于剩余百分比，按比例增加
    if (otherTotal > 0 && remainingPercent >= 0) {
      const scale = remainingPercent / otherTotal;
      updated.forEach((t, i) => {
        if (i !== index) {
          updated[i] = { ...t, percent: Math.round((t.percent || 0) * scale) };
        }
      });
    }

    // 处理舍入误差：确保总和正好是 100%
    const finalTotal = updated.reduce((sum, t) => sum + (t.percent || 0), 0);
    if (finalTotal !== 100 && updated.length > 1) {
      const adjustment = 100 - finalTotal;
      // 将误差加到第一个非当前项的 revision
      for (let i = 0; i < updated.length; i++) {
        if (i !== index && updated[i].percent !== undefined) {
          updated[i] = {
            ...updated[i],
            percent: Math.max(0, Math.min(100, (updated[i].percent || 0) + adjustment)),
          };
          break;
        }
      }
    }

    setEditingTraffic(updated);
  };

  const handleRemove = (index: number) => {
    const updated = editingTraffic.filter((_, i) => i !== index);
    // 重新分配流量，使总和为 100%
    if (updated.length > 0) {
      const remainingPercent = 100;
      const currentTotal = updated.reduce((sum, t) => sum + (t.percent || 0), 0);
      if (currentTotal > 0) {
        const scale = remainingPercent / currentTotal;
        updated.forEach(t => {
          t.percent = Math.round((t.percent || 0) * scale);
        });
      } else {
        // 如果当前总和为 0，平均分配
        const avgPercent = Math.floor(100 / updated.length);
        updated.forEach((t, i) => {
          t.percent = i === updated.length - 1
            ? 100 - (avgPercent * (updated.length - 1))
            : avgPercent;
        });
      }
      // 处理舍入误差
      const finalTotal = updated.reduce((sum, t) => sum + (t.percent || 0), 0);
      if (finalTotal !== 100 && updated.length > 0) {
        updated[0].percent = (updated[0].percent || 0) + (100 - finalTotal);
      }
    }
    setEditingTraffic(updated);
  };

  const handleSaveTraffic = async () => {
    if (!service || !serviceName) {
      setSaveError('服务信息不完整');
      return;
    }

    // 确保总和为 100%
    const total = editingTraffic.reduce((sum, t) => sum + (t.percent || 0), 0);
    if (total !== 100) {
      setSaveError('流量总和必须为 100%');
      return;
    }

    // 过滤掉没有 revision 名称的项
    const validTraffic = editingTraffic.filter(t => t.revisionName);
    if (validTraffic.length === 0) {
      setSaveError('至少需要选择一个 revision');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // 获取当前用户信息
      const identity = await identityApi.getBackstageIdentity();
      const userEntityRef = identity.userEntityRef || 'unknown';

      const targetCluster = clusterName || 'local-cluster';

      // ========== Scoped PATCH（只修改 traffic + 审计注解）==========
      // 对于仅修改流量分配的场景，使用 PATCH 比 PUT 更精准：
      // 1. 完全不碰 spec.template（不会影响容器配置、env、volumes 等）
      // 2. metadata.annotations 在 JSON Merge Patch 中是增量合并（只添加/更新指定 key）
      // 3. payload 更小，只包含实际变更的字段
      const patchPayload = {
        metadata: {
          annotations: {
            [BACKSTAGE_MODIFIED_BY]: userEntityRef,
            [BACKSTAGE_MODIFIED_AT]: new Date().toISOString(),
            [BACKSTAGE_MODIFIED_ACTION]: 'update-traffic',
          },
        },
        spec: {
          traffic: validTraffic.map(t => {
            const trafficTarget: any = {
              percent: t.percent,
            };

            if (t.latestRevision) {
              trafficTarget.latestRevision = true;
            } else if (t.revisionName) {
              trafficTarget.revisionName = t.revisionName;
            }

            if (t.tag) {
              trafficTarget.tag = t.tag;
            }

            return trafficTarget;
          }),
        },
      };

      // PATCH — spec.traffic 是数组，JSON Merge Patch 会整体替换，正好是我们要的行为
      const response = await kubernetesApi.proxy({
        clusterName: targetCluster,
        path: `/apis/serving.knative.dev/v1/namespaces/${serviceNamespace}/services/${serviceName}`,
        init: {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/merge-patch+json',
          },
          body: JSON.stringify(patchPayload),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`更新流量分配失败: ${response.statusText} - ${errorText}`);
      }

      // 更新成功，关闭对话框并刷新数据
      onClose();
      onSaved();
    } catch (err) {
      console.error('Failed to update traffic:', err);
      setSaveError(err instanceof Error ? err.message : '更新流量分配失败');
    } finally {
      setSaving(false);
    }
  };

  const totalPercent = editingTraffic.reduce(
    (sum, t) => sum + (t.percent || 0),
    0,
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>设置流量比例</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {/* 服务名称 */}
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            服务名称:
          </Typography>
          <Typography variant="body1" style={{ fontWeight: 500 }}>
            {service?.metadata.name || '-'}
          </Typography>
        </Box>

        {/* 流量设置表格 */}
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="textSecondary">
              流量设置:
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddRevision}
              disabled={getAvailableRevisions().length === 0}
              color="primary"
              variant="outlined"
            >
              添加
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>修订版本</TableCell>
                  <TableCell align="right" style={{ width: 150 }}>流量比例 %</TableCell>
                  <TableCell style={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {editingTraffic.map((target, index) => {
                  const revisionName = target.revisionName || '';
                  const creationTime = revisionName
                    ? getRevisionCreationTime(revisionName)
                    : '';
                  const availableRevisions = getAvailableRevisions();
                  const allRevisions = sortedRevisions.filter(r =>
                    !target.revisionName || r.metadata.name === target.revisionName
                  );

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <FormControl size="small" style={{ minWidth: 250 }}>
                          <Select
                            value={revisionName || ''}
                            onChange={e => handleRevisionChange(index, e.target.value as string)}
                            displayEmpty
                            variant="outlined"
                          >
                            {!revisionName && (
                              <MenuItem value="" disabled>
                                <em>选择版本</em>
                              </MenuItem>
                            )}
                            {allRevisions.map(revision => (
                              <MenuItem key={revision.metadata.name} value={revision.metadata.name}>
                                <Box>
                                  <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                                    {revision.metadata.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {new Date(revision.metadata.creationTimestamp).toLocaleString('zh-CN', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit',
                                    })}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                            {availableRevisions.map(revision => (
                              <MenuItem key={revision.metadata.name} value={revision.metadata.name}>
                                <Box>
                                  <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                                    {revision.metadata.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {new Date(revision.metadata.creationTimestamp).toLocaleString('zh-CN', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit',
                                    })}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {creationTime && (
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                            {creationTime}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          variant="outlined"
                          value={target.percent || 0}
                          onChange={e =>
                            handleTrafficChange(index, parseInt(e.target.value, 10) || 0)
                          }
                          inputProps={{
                            min: 0,
                            max: 100,
                            style: { textAlign: 'right', paddingRight: 8 },
                          }}
                          InputProps={{
                            endAdornment: <Typography variant="body2" style={{ marginRight: 8 }}>%</Typography>,
                          }}
                          style={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* 总计提示 */}
        <Box mt={2}>
          <Typography
            variant="body2"
            color={totalPercent === 100 ? 'textSecondary' : 'error'}
          >
            总计: {totalPercent}% {totalPercent !== 100 && '(必须为 100%)'}
          </Typography>
        </Box>

        {/* 错误提示 */}
        {saveError && (
          <Box mt={2}>
            <Typography variant="body2" color="error">
              {saveError}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            setSaveError(null);
          }}
          disabled={saving}
        >
          取消
        </Button>
        <Button
          onClick={handleSaveTraffic}
          color="primary"
          variant="contained"
          disabled={saving || totalPercent !== 100 || editingTraffic.some(t => !t.revisionName)}
        >
          {saving ? '保存中...' : '确定'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
